import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import useAPI from '../../../../api';
import { userDataAtom, selectedBranchAtom } from '../../../../Atoms';
import {
  CenteredFlexBox,
  FullSizeColumn,
} from '../../../../Components/FlexBox';
import { checkFeature } from '../../../../Router/routes';
import FormTextField from '../../../../Components/Form/FormTextField';
import FormSelect from '../../../../Components/Form/FormSelect';
import Select from '../../../../Components/Inputs/Select';
import DbeDatePicker from '../../../../Components/Inputs/DbeDatePicker';
import AppointmentSummary from './AppointmentForm/AppointmentSummary';
import getTimeSlots from '../../Utils/getTimeSlots';

const RecallForm = function RecallForm({
  isOperator,
  confirmed = false, // when true shows summary
  phoneNumber,
  sectionId,
  wavePeriod,
}) {
  const intl = useIntl();
  const API = useAPI();
  const {
    reset,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useFormContext();

  const userData = useAtomValue(userDataAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);

  const [timeSlot, setTimeSlot] = useState('');
  const [date, setDate] = useState(new Date());
  const [operators, setOperators] = useState([]);
  const times = getTimeSlots();

  useEffect(() => {
    if (getValues) {
      reset({
        outbound: true,
        phoneNumber,
        sectionId,
        wavePeriod,
        scheduledOn: getValues('scheduledOn') || null,
        assignee: getValues('assignee') || null,
        notes: getValues('notes') || '',
      });
      setTimeSlot(
        getValues('scheduledOn')
          ? getValues('scheduledOn').toTimeString().substring(0, 5)
          : ''
      );
      setDate(
        getValues('scheduledOn')
          ? new Date(getValues('scheduledOn'))
          : new Date()
      );
    }
  }, [phoneNumber, sectionId, wavePeriod]);

  useEffect(() => {
    if (open && !isOperator && selectedBranch) {
      API.getBranchUsersByRole(
        selectedBranch.id,
        ['OPERATOR', 'SUPERVISOR'],
        (result) => {
          if (result && result.length > 0)
            setOperators(
              result.map(({ id, name, surname }) => ({
                label: `${name} ${surname}`,
                value: id,
              }))
            );
          else
            setOperators([
              {
                label: `${userData?.name} ${userData?.surname}`,
                value: userData?.id,
              },
            ]);
        },
        () => {
          setOperators([
            {
              label: `${userData?.name} ${userData?.surname}`,
              value: userData?.id,
            },
          ]);
        }
      );
    } else {
      if (checkFeature('APPOINTMENT_ASSIGNEE_FEATURE'))
        //if is operator and the feature is available
        setOperators([
          {
            label: `${userData?.name} ${userData?.surname}`,
            value: userData?.id,
          },
        ]);
      else setOperators([]);
    }
  }, [open, isOperator]);

  useEffect(() => {
    if (timeSlot) {
      const scheduledDate = new Date(date);
      scheduledDate.setHours(
        parseInt(timeSlot.substring(0, 2), 10),
        parseInt(timeSlot.substring(3, 5), 10)
      );
      setValue('scheduledOn', scheduledDate);
    }
  }, [timeSlot, date]);

  return confirmed ? (
    <AppointmentSummary />
  ) : (
    <FullSizeColumn gap="16px" sx={{ flexGrow: 1 }}>
      {/* Hidden field to track scheduledOn for form validation */}
      <Controller
        name="scheduledOn"
        control={control}
        rules={{
          required: intl.messages.REQUIRED_FIELD || 'This field is required',
        }}
        render={({ field }) => (
          <input type="hidden" {...field} value={field.value || ''} />
        )}
      />
      <CenteredFlexBox gap="8px">
        <DbeDatePicker
          required
          width="50%"
          minDate={new Date()}
          label={intl.messages.DATE}
          date={date}
          onChange={(newDate) => {
            setDate(newDate);
          }}
          variant="outlined"
        />
        <Select
          required
          width="50%"
          error={errors?.scheduledOn}
          label="TIME_SLOT"
          options={times.map((time) => ({
            label: time,
            value: time,
            disabled:
              new Date(
                date?.getFullYear(),
                date?.getMonth(),
                date?.getDate(),
                parseInt(time.substring(0, 2), 10),
                parseInt(time.substring(3, 5), 10)
              )?.getTime() < new Date().getTime(), //disables all the time slots smaller than current time (current time slot included)
          }))}
          value={timeSlot}
          onChange={(newValue) => setTimeSlot(newValue)}
        />
      </CenteredFlexBox>
      <FormSelect
        name="assignee"
        error={errors?.assignee}
        options={[
          {
            label: intl.messages.ALL,
            value: 'ALL',
          },
          ...operators,
        ].filter((x) => x)}
        required
        noHelper
      />
      <FormTextField name="notes" multiline rows={3} />
    </FullSizeColumn>
  );
};

export default RecallForm;
